import React, { useContext, useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"
import { useQuery, useMutation } from "@apollo/client"
import PageHeader from "../../components/PageHeader"
import { LoaderMedium } from "../../components/Loaders"
import { UserContext } from "../../context/userContext"
import { CHANGE_STATUS_ORGANIZATION, GET_ORGANIZATIONS } from "./queries"
import LinkColumn from "../../components/DataTable/Columns/LinkColumn"
import StatusChangeDIalog from "./components/StatusChangeDIalog"
import GenericTable from "../../components/DataTable/GenericTable"
import { useFilters } from "../../components/Filters/useFilters"
import FilterHeader from "../../components/DataTable/FilterHeader"
import { InputSwitch } from "primereact/inputswitch"
import { Link } from "react-router-dom"
import { ElementLoading } from "../../components/ElementLoading"

export default function OrganizationsList() {
  const ID_TEMPLATE = "organization"
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const [organizations, setOrganizations] = useState([])
  const [loadingPage, setLoadingPage] = useState(true)
  const [changeStatusOrganization] = useMutation(CHANGE_STATUS_ORGANIZATION)

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    clearGlobalFilter,
    onFilterChange,
    clearAllFilters,
  } = useFilters()

  const { data, loading: queryLoading } = useQuery(GET_ORGANIZATIONS, {
    variables: {
      staff_id: userCtx.session.user?.staff_id,
      organization_id: userCtx.session.user?.organization_id,
    },
    skip: !userCtx.session.user.staff_id,
    fetchPolicy: "network-only",
  })

  useEffect(() => {
    if (data) {
      setOrganizations(
        _.map(data.organizations, (organization) => {
          return {
            ...organization,
            organization_name:
              organization.organization_id ===
              userCtx.session.user.organization_id ? (
                <strong>
                  {_.unescape(organization.organization_name)} (current)
                </strong>
              ) : (
                _.unescape(organization.organization_name)
              ),
            current_staff_users: organization.current_staff_users,
            current_programs: organization.current_programs,
            current_locations: organization.current_locations,
            edit: (
              <Link
                to={`/enterprise/form/${ID_TEMPLATE}/1/${organization?.organization_id_stamp}`}
                className="btn"
              >
                Edit
              </Link>
            ),
          }
        })
      )
      setLoadingPage(false)
    }
  }, [data, userCtx.session.user.organization_id])

  const filterConfig = [
    {
      label: "Status",
      value: filters.organization_status?.value || "",
      options: [
        { label: "All", value: "" },
        { label: "Active", value: "ACTIVE" },
        { label: "Inactive", value: "INACTIVE" },
      ],
      onChange: (e) => onFilterChange("organization_status", e.value),
      onClear: () => onFilterChange("organization_status", null),
    },
  ]

  if (loadingPage || queryLoading) return <LoaderMedium />

  const columns = [
    {
      field: "organization_name",
      header: "Organization",
      sortable: true,
      body: (rowData) => (
        <LinkColumn
          value={rowData.organization_name}
          to="/"
          itemId={rowData.organization_id}
          text={rowData.organization_name}
          userCtx={userCtx}
          navigate={navigate}
        />
      ),
    },
    {
      field: "current_locations",
      header: "Locations",
      sortable: true,
      body: (rowData) => (
        <LinkColumn
          value={rowData.current_locations}
          to="/organization/locations"
          itemId={rowData.organization_id}
          text={rowData.current_locations}
          userCtx={userCtx}
          navigate={navigate}
        />
      ),
    },
    {
      field: "current_programs",
      header: "Programs",
      sortable: true,
      body: (rowData) => (
        <LinkColumn
          value={rowData.current_programs}
          to="/organization/programs"
          itemId={rowData.organization_id}
          text={rowData.current_programs}
          userCtx={userCtx}
          navigate={navigate}
        />
      ),
    },
    {
      field: "current_staff_users",
      header: "Staff Members",
      sortable: true,
      body: (rowData) => (
        <LinkColumn
          value={rowData.current_staff_users}
          to="/organization/staff"
          itemId={rowData.organization_id}
          text={rowData.current_staff_users}
          userCtx={userCtx}
          navigate={navigate}
        />
      ),
    },
    {
      field: "current_participants",
      header: "Participants",
      sortable: true,
      body: (rowData) => (
        <LinkColumn
          value={rowData.current_participants}
          to="/participant/list"
          itemId={rowData.organization_id}
          text={rowData.current_participants}
          userCtx={userCtx}
          navigate={navigate}
        />
      ),
    },

    {
      field: "status",
      sortable: true,
      header: "Status",
      style: { width: 220 },
      body: (rowData) => <StatusChangeDIalog data={rowData} />,
    },
    { field: "edit", header: "Edit" },
  ]

  return (
    <div className="main-content">
      <PageHeader
        title="Manage Organizations"
        actions={[
          {
            to: "/enterprise/form/organization/1",
            label: "Create new organization",
          },
        ]}
      />
      <GenericTable
        data={organizations}
        columns={columns}
        filters={filters}
        globalFilterValue={globalFilterValue}
        onGlobalFilterChange={onGlobalFilterChange}
        header={
          <FilterHeader
            filters={filterConfig}
            globalFilterValue={globalFilterValue}
            onGlobalFilterChange={onGlobalFilterChange}
            clearGlobalFilter={clearGlobalFilter}
            clearAllFilters={clearAllFilters}
          />
        }
        paginator
        rows={50}
        sortField="organization_name"
        sortOrder={1}
      />
    </div>
  )
}
